import { Box, Button, Slide, Typography } from '@mui/material';
import { RuleGroupInternalType } from '@procureai/query-builder';
import { QueryBuilderProps } from '@procureai/query-builder-ui-mui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpandButton } from '../ExpandButton/ExpandButton';
import { usePopover } from '~/hooks';

import { QuerySavePopover } from './components/QuerySavePopover';
import { ButtonWrap, QueryBuilderContainer, QueryBuilderStyled } from './DataGridQueryBuilder.styled';
import { UseDataGridQueryBuilderConfig } from './hooks/useDataGridQueryBuilderConfig/useDataGridQueryBuilderConfig.types';
import { useDataGridQueryBuilderToggle } from './hooks/useDataGridQueryBuilderToggle/useDataGridQueryBuilderToggle';

export interface DataGridQueryBuilderProps extends Pick<QueryBuilderProps, 'query'> {
  onQueryChange: (query?: RuleGroupInternalType | undefined) => void;
  onSave: (name: string) => void;
  clearDisabled?: boolean;
  saveDisabled?: boolean;
  selectedFilterName?: string;
  useConfig: UseDataGridQueryBuilderConfig;
}

export const DataGridQueryBuilder = ({
  onSave,
  saveDisabled,
  clearDisabled,
  selectedFilterName,
  useConfig,
  query,
  onQueryChange,
}: DataGridQueryBuilderProps) => {
  const { fields, renderValueEditor, getDefaultValue, getOperatorLabel } = useConfig();
  const { t } = useTranslation(undefined, { keyPrefix: 'Base' });
  const { triggerProps, popoverProps } = usePopover();
  const { show, onToggle } = useDataGridQueryBuilderToggle();

  const handleClear = useCallback(() => {
    onQueryChange(undefined);
  }, [onQueryChange]);

  return (
    <>
      <Slide direction="down" in={show}>
        <QueryBuilderContainer>
          {show ? (
            <Box
              data-intercom-target="BaseQueryBuilder"
              sx={{
                mx: 6,
              }}
            >
              <QueryBuilderStyled
                query={query}
                onQueryChange={onQueryChange}
                fields={fields}
                renderValueEditor={renderValueEditor}
                getDefaultValue={getDefaultValue}
                getOperatorLabel={getOperatorLabel}
                fieldsGroupBy={(field) => field.grp}
                showNotToggle={false}
                maxLevels={1}
                combinators={[]}
              />
              <ButtonWrap>
                <Button disabled={clearDisabled} variant="text" onClick={handleClear}>
                  <Typography variant="button">{t('common.builderClear')}</Typography>
                </Button>
                <Button disabled={saveDisabled} variant="text" onClick={triggerProps.onClick}>
                  <Typography variant="button">{t('common.filterSave')}</Typography>
                </Button>
              </ButtonWrap>
            </Box>
          ) : null}
        </QueryBuilderContainer>
      </Slide>
      <ExpandButton
        isExpanded={show}
        expandMoreTitle={t('common.builderShow')}
        expandLessTitle={t('common.builderHide')}
        onToggle={onToggle}
      />
      <QuerySavePopover {...popoverProps} inputValue={selectedFilterName || ''} onSave={onSave} />
    </>
  );
};
